import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Alert, AlertDescription } from '@klaro7/ui';

import { DateOfBirthInput } from '@/components/DateOfBirthInput';
import { PasswordInput } from '@/components/PasswordInput';
import { TurnstileWidget } from '@/components/TurnstileWidget';
import { trackMetaPixelEvent } from '@/features/cookie-consent/analytics/metaPixelEvents';
import { usePublicConfig } from '@/features/settings/hooks/useServicesSettings';

import { PhonePrefixInput } from '@/features/phone-verification/components/PhonePrefixInput';
import { EMAIL_VERIFICATION_PATH } from '@/features/email-verification/utils/emailGate';

import { extractErrorCode } from '@/lib/api';

import { useAuth } from '../hooks/useAuth';

const E164 = /^\+[1-9]\d{6,14}$/;

export function RegisterForm({ redirectTo, initialEmail }: { redirectTo?: string | null; initialEmail?: string | null }) {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { data: publicConfig } = usePublicConfig();

  const [formData, setFormData] = useState({
    email: initialEmail ?? '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
  });
  const [error, setError] = useState<string | null>(null);
  // Errore server sul telefono (numero già associato a un altro account):
  // resta sul campo finché l'utente non cambia numero.
  const [phoneServerError, setPhoneServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(Boolean(initialEmail));
  const [profiling, setProfiling] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  /** Returns the age in completed years for a given ISO date string, or null. */
  const getAge = (iso: string): number | null => {
    if (!iso) return null;
    const dob = new Date(iso);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
    return age;
  };

  const age = getAge(formData.dateOfBirth);
  const isAdult = age !== null && age >= 18;

  const phoneValid = E164.test(formData.phone.trim());
  const turnstileSiteKey = publicConfig?.turnstileSiteKey;
  const canSubmit =
    acceptTerms && acceptPrivacy && isAdult && phoneValid && (!turnstileSiteKey || Boolean(turnstileToken));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    if (!isAdult) {
      setError('Devi avere almeno 18 anni');
      return;
    }

    if (!phoneValid) {
      setError('Inserisci un numero di telefono valido');
      return;
    }

    if (!acceptTerms || !acceptPrivacy) {
      setError('Devi accettare i Termini e Condizioni e l’Informativa Privacy');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        ...formData,
        acceptTerms,
        acceptPrivacy,
        marketing,
        profiling,
        turnstileToken: turnstileSiteKey ? turnstileToken : undefined,
      });
      // Conversione Meta: registrazione completata (no-op senza consenso profilazione,
      // gated dal flag admin — default abilitato).
      if (publicConfig?.metaPixelEvents?.completeRegistration !== false) {
        trackMetaPixelEvent('CompleteRegistration');
      }
      // Doppio opt-in: prima l'email (l'OTP telefono non è nemmeno
      // richiedibile finché non è confermata), poi il telefono — stessa
      // pagina su cui atterra chi rientra più tardi senza aver confermato.
      navigate(EMAIL_VERIFICATION_PATH, { replace: true, state: { redirectTo } });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registrazione fallita. Riprova.';
      setError(message);
      if (extractErrorCode(err) === 'PHONE_ALREADY_USED') setPhoneServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Nome"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="Mario"
          required
        />
        <Input
          label="Cognome"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Rossi"
          required
        />
      </div>

      <Input
        label="Email"
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder="nome@email.com"
        required
        autoComplete="email"
      />

      <PhonePrefixInput
        label="Telefono"
        value={formData.phone}
        onChange={(e164) => {
          setFormData((prev) => ({ ...prev, phone: e164 }));
          setPhoneServerError(null);
        }}
        error={
          formData.phone && !phoneValid
            ? 'Inserisci un numero di telefono valido'
            : phoneServerError ?? undefined
        }
      />

      <p className="-mt-2 text-xs text-neutral-500">
        Attenzione: crea un solo account. I profili duplicati saranno bloccati dal sistema.
      </p>

      <DateOfBirthInput
        name="dateOfBirth"
        value={formData.dateOfBirth}
        onChange={(iso) => setFormData((prev) => ({ ...prev, dateOfBirth: iso }))}
        required
        error={age !== null && !isAdult ? 'Devi avere almeno 18 anni' : undefined}
        helperText="Devi essere maggiorenne per registrarti"
      />

      <PasswordInput
        label="Password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        placeholder="Min. 8 caratteri"
        required
        autoComplete="new-password"
        helperText="Almeno 8 caratteri, una maiuscola, una minuscola e un numero"
      />

      <PasswordInput
        label="Conferma Password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        placeholder="Ripeti la password"
        required
        autoComplete="new-password"
      />

      <div className="space-y-3">
        {/* Mandatory: Terms & Conditions */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="accept-terms"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-white/30 dark:bg-white/10"
          />
          <label htmlFor="accept-terms" className="text-sm text-neutral-600 dark:text-white/60 cursor-pointer">
            Ho letto e accetto i{' '}
            <a href="/condizioni-generali" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700 dark:text-brand-teal dark:hover:text-brand-cyan">
              Termini e Condizioni
            </a>
            {' '}<span className="text-error">*</span>
          </label>
        </div>

        {/* Mandatory: Privacy Policy */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="accept-privacy"
            checked={acceptPrivacy}
            onChange={(e) => setAcceptPrivacy(e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-white/30 dark:bg-white/10"
          />
          <label htmlFor="accept-privacy" className="text-sm text-neutral-600 dark:text-white/60 cursor-pointer">
            Ho letto e accetto l&apos;
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700 dark:text-brand-teal dark:hover:text-brand-cyan">
              Informativa Privacy
            </a>
            {' '}<span className="text-error">*</span>
          </label>
        </div>

        {/* Optional: Marketing */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="accept-marketing"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-white/30 dark:bg-white/10"
          />
          <label htmlFor="accept-marketing" className="text-sm text-neutral-600 dark:text-white/60 cursor-pointer">
            Acconsento a comunicazioni marketing (facoltativo)
          </label>
        </div>

        {/* Optional: Profiling */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="accept-profiling"
            checked={profiling}
            onChange={(e) => setProfiling(e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-white/30 dark:bg-white/10"
          />
          <label htmlFor="accept-profiling" className="text-sm text-neutral-600 dark:text-white/60 cursor-pointer">
            Acconsento alla profilazione (facoltativo)
          </label>
        </div>
      </div>

      {turnstileSiteKey && (
        <TurnstileWidget
          siteKey={turnstileSiteKey}
          onToken={setTurnstileToken}
          onExpire={() => setTurnstileToken('')}
        />
      )}

      <Button type="submit" className="w-full" loading={isLoading} disabled={!canSubmit}>
        Registrati
      </Button>
    </form>
  );
}
